/**
 * Lecture et écriture des montants saisis dans la console (ARTO-58).
 *
 * Règle de la spec (§ 2) : un champ vide vaut `null` — « à saisir » —, jamais
 * zéro ; aucun montant n'est proposé d'office. Les montants voyagent en
 * centimes entiers, jamais en flottants : on lit le texte, on ne calcule pas.
 */

export type Lecture<T> = { ok: true; valeur: T | null } | { ok: false };

/** Tout blanc : `\s` couvre aussi l'espace insécable et la fine insécable des milliers. */
const BLANCS = /\s/g;

/** Nombre décimal de 0 à 2 décimales, `,` ou `.`. Renvoie [entier, centièmes] ou `null`. */
function decouper(texte: string): { entier: string; centiemes: string } | null {
  const correspondance = /^(\d+)(?:[.,](\d{1,2}))?$/.exec(texte);
  if (!correspondance) return null;
  return { entier: correspondance[1]!, centiemes: (correspondance[2] ?? '').padEnd(2, '0') };
}

/** « 1 234,50 » → 123450. Vide → `null`. Tout le reste est refusé. */
export function analyserMontant(saisie: string): Lecture<number> {
  const texte = saisie.replace(BLANCS, '').replace(/€$/, '');
  if (texte === '') return { ok: true, valeur: null };
  const morceaux = decouper(texte);
  if (!morceaux) return { ok: false };
  const centimes = Number(morceaux.entier) * 100 + Number(morceaux.centiemes);
  if (!Number.isSafeInteger(centimes)) return { ok: false };
  return { ok: true, valeur: centimes };
}

/** Des centimes au champ de saisie : `390`, `1234,50`, vide pour « à saisir ». */
export function montantEnSaisie(centimes: number | null): string {
  if (centimes === null) return '';
  const entier = Math.trunc(centimes / 100);
  const reste = centimes % 100;
  return reste === 0 ? String(entier) : `${entier},${String(reste).padStart(2, '0')}`;
}

/** Un entier positif ou nul. Vide → `null`. */
export function analyserEntier(saisie: string): Lecture<number> {
  const texte = saisie.replace(BLANCS, '');
  if (texte === '') return { ok: true, valeur: null };
  if (!/^\d+$/.test(texte)) return { ok: false };
  const valeur = Number(texte);
  return Number.isSafeInteger(valeur) ? { ok: true, valeur } : { ok: false };
}

/** « 33,33 » ou « 10 % » → points de base (10 000 = 100 %). Vide → `null`. */
export function analyserPourcentage(saisie: string): Lecture<number> {
  const texte = saisie.replace(BLANCS, '').replace(/%$/, '');
  if (texte === '') return { ok: true, valeur: null };
  const morceaux = decouper(texte);
  if (!morceaux) return { ok: false };
  const points = Number(morceaux.entier) * 100 + Number(morceaux.centiemes);
  return points <= 10_000 ? { ok: true, valeur: points } : { ok: false };
}

/** Des points de base au champ de saisie : `10`, `33,33`. */
export function pourcentageEnSaisie(points: number | null): string {
  return montantEnSaisie(points);
}
